import type { PartType } from '@/types/rc/miners/PartType'
import { getAllFailureTypes } from '@/services/rc.service'
import { useQuery } from '@tanstack/vue-query'
import { NotifyErrorWrapper } from '@/helpers/core/notifications'

export const useFailureTypes = () => {
  const { data: failureTypes } = useQuery({
    queryKey: ['failure-types'],
    queryFn: () => getAllFailureTypes().catch(NotifyErrorWrapper('Failed to fetch possible failure types')),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  /**
   * Gets the failure types for a given part type
   */
  const getFailureTypes = (partType: PartType) => failureTypes.value?.filter((ft) => ft.part_type === partType)

  /**
   * Gets the failure types for a given part type, formatted for a select dropdown
   */
  const getFailureTypesOptions = (partType: PartType) =>
    getFailureTypes(partType)?.map((ft) => ({ value: ft.id, label: ft.description }))

  /**
   * @param failureTypeId the failure type **id**
   * @returns true if the failure type is 'Other'
   */
  const isFailureTypeOther = (failureTypeId: number) =>
    failureTypes.value?.find((ft) => ft.id === failureTypeId)?.description === 'Other'

  /**
   * @param failureTypeId the failure type **id**
   * @returns true if the failure type is '# ASIC'
   */
  const isFailureTypeASICN = (failureTypeId: number) =>
    failureTypes.value?.find((ft) => ft.id === failureTypeId)?.description === '# ASIC'

  /**
   * Get the failure description for a given failure type id
   */
  const getFailureTypeDescription = (failureTypeId: number) =>
    failureTypes.value?.find((ft) => ft.id === failureTypeId)?.description ?? null

  return {
    failureTypes,
    getFailureTypes,
    getFailureTypesOptions,
    isFailureTypeOther,
    isFailureTypeASICN,
    getFailureTypeDescription,
  }
}
